import { makeStyles } from 'tss-react/mui';

type GlobalSnackbarStackStyleParams = {
  /** Height (px) of the corner already taken by the docked assistant. */
  dockInsetBottomPx: number;
  /** Width (px) of the right edge already taken by the docked assistant. */
  dockInsetRightPx: number;
  /** True when the assistant leaves no corner for the stack to move into. */
  isUnpainted: boolean;
};

const useStyles = makeStyles<GlobalSnackbarStackStyleParams>()(
  (theme, { dockInsetBottomPx, dockInsetRightPx, isUnpainted }) => ({
    stack: {
      position: 'fixed',
      // Anchored to the viewport bottom so the bottom-most widget (device
      // activation) can pin flush with square bottom corners. Floating widgets
      // (e.g. vehicle-import) keep their own `marginBottom` to lift off the edge.
      //
      // The docked assistant claims the same corner and cannot be pushed, so
      // the stack moves clear of whichever edge it has taken, by the amount it
      // occupies plus the spacing the stack already puts between its own
      // widgets. Only one axis is ever non-zero: the dock's resting sizes take
      // the bottom, while the drawer and the short-viewport treatment take the
      // right.
      bottom:
        dockInsetBottomPx > 0
          ? `calc(${dockInsetBottomPx}px + ${theme.new.spacing.medium})`
          : 0,
      right:
        dockInsetRightPx > 0
          ? `calc(${dockInsetRightPx}px + ${theme.new.spacing.large})`
          : theme.new.spacing.large,
      zIndex: theme.zIndex.snackbar,
      // Hiding the container rather than unmounting its children keeps their
      // in-flight progress polling alive; `visibility` also takes the widgets
      // out of the a11y tree while they cannot be seen.
      //
      // It has to be `visibility` and not `display`, because whether there is
      // room for the stack is decided from its measured height: collapsing the
      // box to nothing would report that it fits, paint it, and measure a
      // height that does not fit again on every frame.
      visibility: isUnpainted ? 'hidden' : 'visible',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: theme.new.spacing.medium,
      // The stack only frames the corner; empty space must not swallow clicks,
      // while each rendered widget re-enables them. Applying pointer-events per
      // rendered child (not a wrapper) means a widget that renders null leaves no
      // placeholder flex item — so `gap` never adds phantom spacing.
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
      // Only the true last child ever sits flush with the viewport edge — any
      // earlier child (e.g. a second activation-progress card stacked above
      // another) floats above a gap and keeps all four corners rounded,
      // overriding whatever corner radii the widget itself hardcoded. Once the
      // dock claims the edge, the last child floats too, so it is included.
      [`& > *${dockInsetBottomPx > 0 ? '' : ':not(:last-child)'}`]: {
        borderBottomLeftRadius: theme.new.shape.borderRadius.medium,
        borderBottomRightRadius: theme.new.shape.borderRadius.medium,
      },
    },
  })
);

export default useStyles;
